import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';

/**
 * MediaCarousel
 * - Uniform 9:16 TikTok-style cards
 * - 1 visible mobile / 2 tablet / 4 desktop (CSS-driven scroll-snap)
 * - Arrow controls scroll the rail by one "page" worth of items
 * - Videos open in a lightbox modal on click; images open in fullscreen viewer
 */
export default function MediaCarousel({ items = [] }) {
  const railRef = useRef(null);
  const [active, setActive] = useState(null); // {type, src, poster, title}
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const scrollByPage = (dir) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.92, behavior: 'smooth' });
  };

  // Close lightbox on Escape
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => { if (e.key === 'Escape') setActive(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  return (
    <div className="relative" data-testid="media-carousel">
      {/* Rail */}
      <div
        ref={railRef}
        className="flex gap-3 sm:gap-4 overflow-x-auto pb-3 snap-x snap-mandatory scroll-smooth scrollbar-hide"
        style={{ scrollbarWidth: 'none' }}
      >
        {items.map((it, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => setActive(it)}
            data-testid={`carousel-item-${idx}`}
            className="group relative flex-shrink-0 snap-start w-[calc(100%-0px)] sm:w-[calc(50%-8px)] lg:w-[calc(25%-12px)] aspect-[9/16] rounded-xl overflow-hidden bg-black shadow-md border border-border focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label={it.title || (it.type === 'video' || it.type === 'youtube' ? 'Play video' : 'View image')}
          >
            <img
              src={it.poster}
              alt={it.title || ''}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            {it.type === 'video' || it.type === 'youtube' ? (
              <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:bg-white transition-colors">
                  <Play className="w-6 h-6 sm:w-7 sm:h-7 text-primary fill-primary ml-1" />
                </span>
              </span>
            ) : null}
            <span className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 to-transparent pointer-events-none" />
          </button>
        ))}
      </div>

      {/* Arrows */}
      <button
        type="button"
        onClick={() => scrollByPage(-1)}
        disabled={!canPrev}
        data-testid="carousel-prev"
        aria-label="Previous"
        className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 hidden sm:flex w-11 h-11 rounded-full bg-white border border-border shadow-md items-center justify-center transition-all ${canPrev ? 'hover:bg-primary hover:text-white hover:border-primary cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={() => scrollByPage(1)}
        disabled={!canNext}
        data-testid="carousel-next"
        aria-label="Next"
        className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 hidden sm:flex w-11 h-11 rounded-full bg-white border border-border shadow-md items-center justify-center transition-all ${canNext ? 'hover:bg-primary hover:text-white hover:border-primary cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Mobile arrows (inline below rail) */}
      <div className="flex sm:hidden justify-center gap-3 mt-2">
        <button
          type="button"
          onClick={() => scrollByPage(-1)}
          disabled={!canPrev}
          aria-label="Previous"
          data-testid="carousel-prev-mobile"
          className={`w-10 h-10 rounded-full bg-white border border-border shadow flex items-center justify-center ${canPrev ? '' : 'opacity-40'}`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={() => scrollByPage(1)}
          disabled={!canNext}
          aria-label="Next"
          data-testid="carousel-next-mobile"
          className={`w-10 h-10 rounded-full bg-white border border-border shadow flex items-center justify-center ${canNext ? '' : 'opacity-40'}`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setActive(null)}
          data-testid="carousel-lightbox"
        >
          <button
            type="button"
            onClick={() => setActive(null)}
            aria-label="Close"
            data-testid="carousel-close"
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-[95vw] max-h-[92vh] w-auto h-auto rounded-xl overflow-hidden shadow-2xl bg-black"
          >
            {active.type === 'video' ? (
              <video
                src={active.src}
                poster={active.poster}
                controls
                autoPlay
                playsInline
                className="block max-h-[92vh] max-w-[95vw] w-auto h-auto"
                style={{ aspectRatio: '9/16' }}
              />
            ) : active.type === 'youtube' ? (
              <div className="relative" style={{ width: 'min(95vw, 540px)', aspectRatio: '9/16' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${active.youtubeId}?autoplay=1&rel=0&playsinline=1`}
                  title={active.title || 'Video'}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            ) : (
              <img
                src={active.src || active.poster}
                alt={active.title || ''}
                className="block max-h-[92vh] max-w-[95vw] w-auto h-auto"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
